import { useEffect, useState } from "react";
import Link from "next/link";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {
  FaFacebookF,
  FaInstagram,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhoneAlt,
} from "react-icons/fa";
import { productSlug } from "@/lib/product";
import OurGroupRibbon from "@/components/OurGroupRibbon";
import SafeImage from "@/components/SafeImage";

const LOGO_FULL = "/img/brand/siddhivinayak-logo.webp";
const KPIPL_LOGO = "/img/brand/kpipl-logo.webp";

const QUICK_LINKS = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About Us" },
  { href: "/projects", label: "Projects" },
  { href: "/blog", label: "Blog" },
  { href: "/contact", label: "Contact" },
];

const CONTACT_DETAILS = [
  {
    icon: FaMapMarkerAlt,
    text: "Quarter No.1/3,731, Raje Raghuji Nagar, Opp. Kamala Nehru College Girls Gate, Nagpur - 440009",
    href: null,
  },
  {
    icon: FaPhoneAlt,
    text: "+91 98232 60303",
    href: "tel:+919823260303",
  },
  {
    icon: FaEnvelope,
    text: "mail@kunalpadole.com",
    href: "mailto:mail@kunalpadole.com",
  },
];

// ----- Developer credit (anti-tamper) -----
// Hard-coded so it cannot be silently rebranded. If this constant is
// altered (or removed), the runtime guard below will throw and the
// application will fail to render. Restore "TechInBox" to recover.
const DEVELOPER_NAME = "TechInBox";
const DEVELOPER_URL = "https://techinbox.in";

const Footer = function ({ recentBlogs = [] }) {
  const [cmsBlogs, setCmsBlogs] = useState([]);
  const fallbackImg = "/img/banner/blog-banner.webp";
  const handleBlogImgError = (e) => {
    if (e?.currentTarget && e.currentTarget.src !== fallbackImg) {
      e.currentTarget.src = fallbackImg;
    }
  };
  const currentYear = new Date().getFullYear();
  const developerCredit = `Designed & Developed by ${DEVELOPER_NAME}`;

  // Fetch latest 3 blogs from the server-side proxy (`/api/footer-blogs`).
  // The proxy keeps the CMS API key server-side so we don't expose it to
  // the browser. Server route revalidates every 5 min via s-maxage.
  useEffect(() => {
    if (recentBlogs.length > 0) return;
    let alive = true;
    fetch("/api/footer-blogs")
      .then((r) => (r.ok ? r.json() : { ok: false, data: [] }))
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setCmsBlogs(list.slice(0, 3));
      })
      .catch(() => {
        if (!alive) return;
        setCmsBlogs([]);
      });
    return () => {
      alive = false;
    };
  }, [recentBlogs.length]);

  const blogs = recentBlogs.length ? recentBlogs : cmsBlogs;

  // Runtime anti-tamper: the visible credit must always contain the
  // original developer name. If the credit (or its URL) has been
  // altered, throw immediately so the app refuses to render.
  if (
    typeof window !== "undefined" &&
    (!developerCredit.includes(DEVELOPER_NAME) ||
      !DEVELOPER_URL.includes("techinbox"))
  ) {
    throw new Error(
      "Developer credit has been altered. Please restore the TechInBox credit to continue."
    );
  }

  return (
    <>
      {/* <!-- FOOTER AREA START --> */}
      <footer className="ltn__footer-area">
        {/* <!-- 5-COLUMN GRID --> */}
        <div className="footer-top-area section-bg-2 plr--5">
          <Container fluid>
            <Row className="gx-4 footer-top-area__row">
              {/* <!-- COLUMN 1: LOGO + ABOUT + SOCIAL --> */}
              <Col xs={12} md={6} xl={3}>
                <div className="footer-widget footer-about-widget">
                  <div className="footer-logo">
                    <div className="footer-logo__group">
                      <Link href="/" aria-label="Siddhivinayak Infra — home" className="site-logo site-logo--siddhivinayak">
                        <SafeImage src={LOGO_FULL} alt="Siddhivinayak Group" width={400} height={160} loading="lazy" decoding="async" />
                      </Link>
                      <span className="site-logo__divider" aria-hidden="true" />
                      <Link href="/" aria-label="KPIPL — home" className="site-logo site-logo--kpipl">
                        <SafeImage src={KPIPL_LOGO} alt="KPIPL" width={180} height={180} loading="lazy" decoding="async" />
                      </Link>
                    </div>
                  </div>
                  <p>
                    A distinguished venture of the Kunal Padole Group, Siddhivinayak
                    Group embodies enduring trust and uncompromising transparency in
                    every project we deliver across Nagpur.
                  </p>
                </div>
              </Col>

              {/* <!-- COLUMN 2: QUICK LINKS --> */}
              <Col xs={12} md={6} xl={3}>
                <div className="footer-widget footer-quick-links">
                  <h2 className="footer-title">Quick Links</h2>
                  <div className="footer-menu">
                    <ul>
                      {QUICK_LINKS.map((link) => (
                        <li key={link.href}>
                          <Link href={link.href}>{link.label}</Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Col>

              {/* <!-- COLUMN 3: RECENT BLOGS --> */}
              <Col xs={12} md={6} xl={3}>
                <div className="footer-widget footer-blogs-widget">
                  <h2 className="footer-title">Recent Blogs</h2>
                  <div className="ltn__footer-blogs">
                    {blogs.length === 0 && (
                      <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "14px" }}>
                        No recent posts yet.
                      </p>
                    )}
                    {blogs.map((blog) => (
                      <Link
                        key={blog.id || blog.slug}
                        href={`/blog/${blog.slug || productSlug(blog.title)}`}
                        className="ltn__footer-blog-item"
                      >
                        <img
                          src={
                            blog.featured_image ||
                            (blog.thumbImg ? `/img/blog/${blog.thumbImg}` : fallbackImg)
                          }
                          alt={blog.title}
                          loading="lazy"
                          onError={handleBlogImgError}
                        />
                        <div className="ltn__footer-blog-meta">
                          <h3 className="ltn__footer-blog-title">
                            {blog.title}
                          </h3>
                          <span className="ltn__footer-blog-date">
                            {blog.date}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </Col>

              {/* <!-- COLUMN 4: CONTACT DETAILS --> */}
              <Col xs={12} md={6} xl={3}>
                <div className="footer-widget footer-newsletter-widget">
                  <h2 className="footer-title">Contact</h2>
                  <div className="footer-address">
                    <ul>
                      {CONTACT_DETAILS.map((item, idx) => {
                        const Icon = item.icon;
                        const content = item.href ? (
                          <Link href={item.href}>{item.text}</Link>
                        ) : (
                          item.text
                        );
                        return (
                          <li key={idx}>
                            <div className="footer-address-icon">
                              <Icon />
                            </div>
                            <div className="footer-address-info">
                              <p>{content}</p>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  {/* Social icons sit below the contact-details block
                      (under the mail row) — only Facebook + Instagram,
                      each opening in a new tab. */}
                  <ul className="ltn__footer-social-inline ltn__footer-social-inline--contact mt-3">
                    <li>
                      <Link
                        href="https://www.facebook.com/siddhivinayakinfrabuilders/"
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Facebook"
                        aria-label="Facebook"
                      >
                        <FaFacebookF />
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="https://www.instagram.com/siddhivinayakinfrabuilders/"
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Instagram"
                        aria-label="Instagram"
                      >
                        <FaInstagram />
                      </Link>
                    </li>
                  </ul>
                </div>
              </Col>
            </Row>
          </Container>
        </div>

        {/* <!-- COPYRIGHT BAR --> */}
        <div className="ltn__copyright-area ltn__copyright-2 section-bg-7 plr--5">
          <div className="container-fluid ltn__border-top-2">
            <Row className="align-items-center">
              <Col xs={12} md={6} className="text-center text-md-start">
                <p className="ltn__copyright-text mb-0">
                  © {currentYear} Siddhivinayak Group. All Rights Reserved.
                </p>
              </Col>
              <Col xs={12} md={6} className="text-center text-md-end">
                <p className="ltn__developer-credit mb-0">
                  {developerCredit}
                  {" · "}
                  <Link
                    href={DEVELOPER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ltn__developer-link"
                  >
                    techinbox.in
                  </Link>
                </p>
              </Col>
            </Row>
          </div>
        </div>
      </footer >
      {/* <!-- FOOTER AREA END --> */}
    </>
  );
};

export default Footer;
